import React from 'react'
import { NotificationsActiveOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { cellErrorDescription } from '../generic/utilities/cellErrorDescription'
import { systemValueCodeLabel } from '../generic/systemValues/systemValueLabel'
import { normalizeLevel, getDisplayDescription } from './sensorAlarmUtils'
import { alarmLevelVisual } from './machineAlarmUtils'
import { formatClock, formatElapsedSince } from './byGroupVisuals'

const EM_DASH = '—'

/**
 * MachineAlarmsPanel — the machine-detail alarms panel (contract §10.2), served on
 * demand from `monitoring/deviceAlarms/{deviceId}`. Read-only for now (Acknowledge,
 * §10.3, does not exist yet — phase 2).
 *
 * Sun already sorts the list (ERR → WAR → INF, newest first within each severity), so
 * the panel renders it in order without re-sorting. Each row shows a left severity rail
 * + level chip + code + start time + live DURATION (computed in the front from
 * `startedAtMillis`) and, when it adds information, the description. Empty list → the
 * "No active alarms" state.
 *
 * @param {{alarms?: object[], testId?: string}} props
 */
export default function MachineAlarmsPanel ({ alarms = [], testId = 'asset-detail-alarms' }) {
  const { t_ } = useSmartTranslation()
  return (
    <div className='mcx-teltrip' data-testid={testId}>
      <div className='mcx-teltrip__head'>
        <NotificationsActiveOutlined sx={{ fontSize: 18 }} />
        <span className='mcx-label-caps mcx-label-caps--xs'>
          {t_('Alarms')}{alarms.length > 0 ? ` (${alarms.length})` : ''}
        </span>
      </div>
      {alarms.length === 0
        ? (
          <p className='mcx-data-panel__value mcx-data-panel__value--empty' data-testid={`${testId}-empty`}>
            {t_('No active alarms')}
          </p>
          )
        : (
          <div className='mcx-alarms'>
            {alarms.map((alarm, index) => {
              const level = normalizeLevel(alarm.level)
              const visual = alarmLevelVisual(alarm.level)
              // Story 162, CA13: a machine reporting the cell's PLC codes shows them in the user's language.
              const description = getDisplayDescription({ ...alarm, description: cellErrorDescription(t_, alarm) })
              const duration = formatElapsedSince(alarm.startedAtMillis)
              return (
                <div
                  key={alarm.id ?? index}
                  className={`mcx-alarmrow ${visual.railClass}`}
                  data-testid={`${testId}-row-${index}`}
                >
                  <div className='mcx-alarmrow__main'>
                    <span className={`mcx-alarm-chip ${visual.chipClass}`}>{systemValueCodeLabel(t_, 'ErrorLevelEnum', level)}</span>
                    <span className='mcx-alarmrow__code'>{alarm.code || EM_DASH}</span>
                    {alarm.startedAtMillis ? <span className='mcx-alarmrow__time'>{formatClock(alarm.startedAtMillis)}</span> : null}
                    {duration ? <span className='mcx-alarmrow__dur'>{duration}</span> : null}
                  </div>
                  {description && <div className='mcx-alarmrow__desc'>{description}</div>}
                </div>
              )
            })}
          </div>
          )}
    </div>
  )
}
